import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { purgeStoredState } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import {
  apiSignIn,
  getLoggedInUser
} from '@lok/services/auth';
import { useCreateLogMutation } from '@lok/services/logs';
import { isValidToken, isTokenUnexpired } from '@lok/utils/auth';
import RtkQueryService from '@lok/services/RtkQueryService';
import useModuleStore from '@lok/pages/modules/store/lessonStore';

import {
  signInSuccess,
  signOutSuccess
} from '../store/auth/sessionSlice';
import { setUser } from '../store/auth/userSlice';

const useAuth = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [createLog] = useCreateLogMutation();
  const { removeLessons } = useModuleStore();

  const { token, signedIn, refreshToken } = useSelector(
    (state) => state.auth.session
  );
  const [initializing, setInitializing] = useState(true);

  // Authenticated if either the access token is still good OR the refresh
  // token is — BaseService refreshes silently on the first protected call
  // when only the latter is true. Without the refresh-token leg here, a user
  // whose access token expired overnight gets bounced to /login before the
  // refresh ever has a chance to fire.
  const isTokenOK = isValidToken(token);
  const canRecoverSession =
    isTokenUnexpired(token) || isTokenUnexpired(refreshToken);

  const authenticated = Boolean(
    token && signedIn && isTokenOK && canRecoverSession
  );

  useEffect(() => {
    // Once token is either defined or null, we're done initializing
    if (token !== undefined) {
      setInitializing(false);
    }
  }, [token]);

  const getUser = async () => {
    try {
      const resp = await getLoggedInUser();
      dispatch(setUser(resp.data));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching user:', error);
    }
  };

  const signIn = async (credentials) => {
    try {
      const resp = await apiSignIn(credentials);
      const { access, refresh } = resp.data;

      // Store both access and refresh tokens
      dispatch(signInSuccess({
        access,
        refresh
      }));

      return {
        ...resp,
        authenticated: true
      };
    } catch (error) {
      return {
        authenticated: false,
        status: 'error',
        message: error.message,
        error_object: error?.response?.data
      };
    }
  };

  const signOut = async () => {
    // Step 1: Clear persisted Redux state
    await purgeStoredState({ storage });

    // Step 2: Reset RTK Query cache
    dispatch(RtkQueryService.util.resetApiState());

    // Step 3: Remove custom memory state
    removeLessons();

    // Step 4: Inform state of sign-out
    dispatch(signOutSuccess());

    // Step 5: Clear user data
    dispatch(
      setUser({
        userName: '',
        email: '',
        picture: '',
        address: '',
        title: '',
        education: '',
        about_me: '',
        country: '',
        type: '',
        id: '',
        cover_picture: '',
        first_name: '',
        last_name: '',
        nickname: ''
      })
    );

    // Step 6: Log logout event
    await createLog({
      page: 'logout',
      event_type: 'logged_out'
    });

    // Step 7: Force full app reload (critical!)
    localStorage.removeItem('activeQuizSession');
    localStorage.removeItem('admin');

    // Step 8: Redirect to login page
    navigate('/login');
  };

  return {
    token,
    authenticated,
    initializing,
    signIn,
    signOut,
    getUser
  };
};

export default useAuth;
